import { defaultVisualDesign, documentVisualOf, normalizeVisualDesign, type DocumentVisual, type StudioAsset, type TemplatedDocument, type VisualDesign } from "../../domain/studio/visual-design.js";
import type { StudioDesignRepository } from "../../repositories/studio-design-repository.js";
import type { DocumentStorageService } from "./document-storage-service.js";

/**
 * Leitura INTERNA da identidade publicada, para quem monta documento e para
 * a própria fachada. Não confere permissão e por isso NUNCA entra no RPC.
 *
 * Sem nenhuma publicação ainda, a versão vigente é a V0: os padrões, que são
 * exatamente o visual de hoje.
 */
export class StudioDesignProvider {
  private cached: { design: VisualDesign; version: number; loadedAt: number } | null = null;
  private static readonly TTL_MS = 30_000;

  constructor(private readonly repo: StudioDesignRepository, private readonly storage: DocumentStorageService) {}

  async published(): Promise<{ design: VisualDesign; version: number }> {
    if (this.cached && Date.now() - this.cached.loadedAt < StudioDesignProvider.TTL_MS) return this.cached;
    const latest = await this.repo.latestVersion();
    this.cached = { design: latest ? normalizeVisualDesign(latest.payload) : defaultVisualDesign(), version: latest?.version ?? 0, loadedAt: Date.now() };
    return this.cached;
  }

  /** Publicar/restaurar invalida o cache: a próxima leitura já traz a versão nova, sem deploy. */
  invalidate(): void {
    this.cached = null;
  }

  async resolveAsset(asset: StudioAsset | null): Promise<string | null> {
    if (!asset?.ref) return null;
    return (await this.storage.resolveDownloadUrl(asset.ref).catch(() => null)) ?? null;
  }

  /** Visual de uma versão PUBLICADA específica (imutável); V0 = padrão. */
  async documentVisualForVersion(type: TemplatedDocument | null, version: number): Promise<DocumentVisual> {
    const design = version === 0 ? defaultVisualDesign() : normalizeVisualDesign((await this.repo.getVersion(version))?.payload ?? null);
    const logo = design.documents.logoMode === "IMAGEM" ? await this.resolveAsset(design.logos.document) : null;
    return documentVisualOf(design, type, version, logo);
  }

  /** Carimba a versão vigente num documento (o primeiro carimbo vale para sempre). */
  async stampDocument(documentType: string, documentId: string): Promise<number> {
    const { version } = await this.published();
    return this.repo.stampDocument(documentType, documentId, version);
  }

  /**
   * Visual de um documento SEM modelo congelado (RI): carimbado ⇒ a versão
   * do carimbo; fechado sem carimbo ⇒ V0 (anterior ao Studio); aberto ⇒ a
   * versão vigente.
   */
  async visualForDocument(type: TemplatedDocument, documentId: string, closed: boolean): Promise<DocumentVisual> {
    const stamp = await this.repo.documentStamp(type, documentId);
    if (stamp !== null) return this.documentVisualForVersion(type, stamp);
    if (closed) return this.documentVisualForVersion(type, 0);
    return this.documentVisual(type);
  }

  /**
   * O visual que o serviço de cada relatório grava DENTRO do modelo
   * documental. Sai da versão PUBLICADA — rascunho nunca chega a documento.
   */
  async documentVisual(type: TemplatedDocument): Promise<DocumentVisual> {
    const { design, version } = await this.published();
    const logo = design.documents.logoMode === "IMAGEM" ? await this.resolveAsset(design.logos.document) : null;
    return documentVisualOf(design, type, version, logo);
  }
}
